import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../../context/useAuth';

const navSections = [
  {
    label: 'Overview',
    links: [
      { to: '/admin/dashboard', icon: 'ri-dashboard-3-line', label: 'Dashboard' },
    ],
  },
  {
    label: 'Content',
    links: [
      { to: '/admin/courses', icon: 'ri-book-3-line', label: 'Courses' },
    ],
  },
];

const breadcrumbMap = {
  '/admin/dashboard': 'Dashboard',
  '/admin/courses': 'Courses',
};

function getBreadcrumb(pathname) {
  const crumbs = [{ label: 'Admin', to: '/admin/dashboard' }];
  if (pathname.includes('/admin/courses/new')) {
    crumbs.push({ label: 'Courses', to: '/admin/courses' });
    crumbs.push({ label: 'New Course', to: '/admin/courses/new' });
  } else if (pathname.match(/\/admin\/courses\/.+\/edit/)) {
    crumbs.push({ label: 'Courses', to: '/admin/courses' });
    crumbs.push({ label: 'Edit Course', to: pathname });
  } else if (breadcrumbMap[pathname]) {
    crumbs.push({ label: breadcrumbMap[pathname], to: pathname });
  }
  return crumbs;
}

function getPageTitle(pathname) {
  if (pathname === '/admin/dashboard') return 'Dashboard';
  if (pathname === '/admin/courses') return 'Manage Courses';
  if (pathname.includes('/admin/courses/new')) return 'Create New Course';
  if (pathname.match(/\/admin\/courses\/.+\/edit/)) return 'Edit Course';
  return 'Admin';
}

export default function AdminLayout() {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();

  // if (!isAuthenticated || (user?.role !== 'admin' && user?.role !== 'instructor')) {
  //   return <Navigate to="/login" replace />;
  // }

  const breadcrumbs = getBreadcrumb(location.pathname);
  const pageTitle = getPageTitle(location.pathname);

  return (
    <div className="flex min-h-screen" style={{ background: '#f0f2f5' }}>
      {/* Sidebar */}
      <aside
        className="w-64 flex-shrink-0 fixed top-0 left-0 bottom-0 z-40 flex flex-col"
        style={{ background: 'linear-gradient(160deg, #0f172a 0%, #1e293b 100%)' }}
      >
        {/* Brand */}
        <div className="px-6 pt-6 pb-5" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
          <Link to="/" className="block">
            <img
              src="https://public.readdy.ai/ai/img_res/a37d52a8-201a-4b92-9b36-08215345d1df.png"
              alt="LearnFlow"
              className="h-8 w-auto"
            />
          </Link>
          <div className="mt-3 flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1 rounded-full"
              style={{ background: 'rgba(20,184,166,0.18)', color: '#2dd4bf' }}
            >
              <span
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{ background: '#2dd4bf' }}
              />
              {user?.role === 'admin' ? 'Admin Panel' : 'Instructor Panel'}
            </span>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-4 py-5 overflow-y-auto space-y-6">
          {navSections.map(section => (
            <div key={section.label}>
              <p
                className="text-xs uppercase font-bold tracking-widest px-3 mb-2"
                style={{ color: 'rgba(148,163,184,0.5)' }}
              >
                {section.label}
              </p>
              <div className="space-y-0.5">
                {section.links.map(link => {
                  const isActive =
                    link.to === '/admin/dashboard'
                      ? location.pathname === '/admin/dashboard'
                      : location.pathname.startsWith(link.to);
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        isActive
                          ? 'text-white'
                          : 'hover:bg-white/5'
                      }`}
                      style={
                        isActive
                          ? { background: 'linear-gradient(90deg, #0d9488, #0f766e)', color: '#fff' }
                          : { color: 'rgba(148,163,184,0.85)' }
                      }
                    >
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                        style={
                          isActive
                            ? { background: 'rgba(255,255,255,0.18)' }
                            : { background: 'rgba(255,255,255,0.05)' }
                        }
                      >
                        <i className={`${link.icon} text-base`} />
                      </div>
                      {link.label}
                      {isActive && (
                        <span className="ml-auto w-1.5 h-1.5 rounded-full bg-teal-300" />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom user card */}
        <div className="p-4" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
          {user && (
            <div
              className="flex items-center gap-3 p-3 rounded-xl mb-3"
              style={{ background: 'rgba(255,255,255,0.06)' }}
            >
              <img
                src={user.avatar}
                alt={user.name}
                className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                style={{ border: '2px solid rgba(45,212,191,0.4)' }}
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-semibold truncate">{user.name}</p>
                <p
                  className="text-xs capitalize truncate"
                  style={{ color: 'rgba(148,163,184,0.7)' }}
                >
                  {user.email}
                </p>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Link
              to="/"
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
              style={{
                background: 'rgba(255,255,255,0.06)',
                color: 'rgba(148,163,184,0.8)',
              }}
            >
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-left-line" />
              </div>
              Back to Site
            </Link>
            <button
              onClick={logout}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap"
              style={{
                background: 'rgba(239,68,68,0.1)',
                color: 'rgba(252,165,165,0.9)',
              }}
            >
              <div className="w-3 h-3 flex items-center justify-center">
                <i className="ri-logout-box-line" />
              </div>
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="ml-64 flex-1 flex flex-col min-h-screen">
        {/* Top header */}
        <header
          className="sticky top-0 z-30 flex items-center justify-between px-8 py-4"
          style={{
            background: 'rgba(240,242,245,0.95)',
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(226,232,240,0.8)',
          }}
        >
          <div>
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1.5 text-xs mb-1" aria-label="Breadcrumb">
              {breadcrumbs.map((crumb, i) => (
                <span key={crumb.to} className="flex items-center gap-1.5">
                  {i > 0 && (
                    <i className="ri-arrow-right-s-line" style={{ color: '#94a3b8' }} />
                  )}
                  {i < breadcrumbs.length - 1 ? (
                    <Link
                      to={crumb.to}
                      className="hover:text-teal-600 transition-colors cursor-pointer"
                      style={{ color: '#94a3b8' }}
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="font-semibold" style={{ color: '#0f172a' }}>
                      {crumb.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
            <h1 className="text-xl font-bold" style={{ color: '#0f172a' }}>
              {pageTitle}
            </h1>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/admin/courses/new"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap text-white"
              style={{ background: 'linear-gradient(90deg, #0d9488, #0f766e)' }}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line" />
              </div>
              New Course
            </Link>
            {user && (
              <div className="flex items-center gap-2.5 pl-3" style={{ borderLeft: '1px solid #e2e8f0' }}>
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-8 h-8 rounded-full object-cover"
                  style={{ border: '2px solid #e2e8f0' }}
                />
                <span className="text-sm font-semibold" style={{ color: '#1e293b' }}>
                  {user.name}
                </span>
              </div>
            )}
          </div>
        </header>

        <main className="flex-1 p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
